import type { Build, BuildArtefact } from '../api/types';
import type { FieldError } from '../api/validate';

/**
 * The one build rule the schema cannot express here: a build must be chosen. A sha256 is optional (FR-11 amended
 * 2026-10-05): without one the build is verified on first fetch, so a missing checksum is never an error.
 */
export function missingBuild(build: Build | undefined, path = 'build'): FieldError[] {
  return build ? [] : [{ path, message: 'is required' }];
}

type Listed = BuildArtefact;

/** A listed build is offered unless releasemanager marks its release disabled (the API already leaves those out). */
export function offered(items: Listed[]): Listed[] {
  return items.filter((b) => b.release?.enabled !== false);
}

/** `2026-10-07T15:10:00Z` → `2026-10-07 15:10 UTC`; anything else as it is. */
export function shortTime(t: string | undefined): string {
  if (!t) return '';
  const m = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/.exec(t);
  return m ? `${m[1]} ${m[2]} UTC` : t;
}

export function shortCommit(c: string | undefined): string {
  return c ? c.slice(0, 8) : '';
}

function size(n: number): string {
  return n > 1e9 ? `${(n / 1e9).toFixed(1)} GB` : `${(n / 1e6).toFixed(0)} MB`;
}

/**
 * One line per build in the picker. From MinIO: `#pipeline · file · size · time`. From releasemanager: the release
 * name, `#build`, short commit, time and pipeline status, whichever are known.
 */
export function buildLabel(b: Listed): string {
  const r = b.release;
  const parts = r
    ? [r.name, `#${b.pipeline}`, shortCommit(r.commit), shortTime(r.releaseTime ?? b.lastModified), r.pipelineStatus && `pipeline ${r.pipelineStatus}`]
    : [`#${b.pipeline}`, b.file, b.sizeBytes == null ? '' : size(b.sizeBytes), shortTime(b.lastModified)];
  return parts.filter((p): p is string => !!p).join(' · ');
}

/** A link target from another system's data, only when it is http(s) (never `javascript:` and the like). */
export function httpHref(u: string | undefined): string | undefined {
  return u && /^https?:\/\//i.test(u) ? u : undefined;
}
